/**
 * Typed client for the control API.
 *
 * Note what is NOT here: any copy of the production rule. The UI never decides for
 * itself whether a run is permitted — it renders `decision.allowed` as served by
 * `/api/matrix`, and the API refuses independently. Duplicating the rule in the
 * browser is how the two drift and the UI starts offering something the server will
 * reject (or worse, hiding something it would allow).
 */

export type EnvironmentName = 'dev' | 'qa' | 'prod';
export type PresetName = 'smoke' | 'unit' | 'regression';
export type RunStatus = 'queued' | 'running' | 'passed' | 'failed' | 'stopped' | 'error';

export interface Decision {
  allowed: boolean;
  reason?: string;
}

export interface MatrixButton {
  target: { environment: EnvironmentName; preset: PresetName; scope: string };
  label: string;
  description: string;
  expectedDuration: string;
  decision: Decision;
  watch: Decision;
}

export interface AppDecl {
  scope: string;
  label: string;
  specPaths: string[];
  unitRepo?: string;
}

export interface MatrixRow {
  environment: EnvironmentName;
  buttons: MatrixButton[];
  scopes: AppDecl[];
}

export interface RunSummary {
  id: string;
  environment: EnvironmentName;
  preset: PresetName;
  presetLabel: string;
  scope: string;
  scopeLabel: string;
  status: RunStatus;
  watch: boolean;
  vncUrl?: string;
  triggeredBy: string;
  queuedAt: string;
  startedAt?: string;
  finishedAt?: string;
  queueReason?: string;
  stoppedBy?: string;
  counts: { total: number; passed: number; failed: number; skipped: number; unstable: number };
}

export interface LogLine {
  seq: number;
  at: string;
  stream: 'stdout' | 'stderr' | 'event';
  text: string;
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly refusedBy?: string) {
    super(message);
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    // The session is the CAIT cookie on the shared parent domain.
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });

  const text = await response.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    body = { error: text };
  }

  if (!response.ok) {
    const err = body as { error?: string; refusedBy?: string };
    throw new ApiError(err.error ?? `Request failed (${response.status})`, response.status, err.refusedBy);
  }

  return body as T;
}

export interface Me {
  user: { id: string; email: string; role: string };
  allowed: boolean;
  requiredRole: string;
}

export const api = {
  /** Who am I? Drives the choice between the app and the sign-in screen. */
  me: () => call<Me>('/api/me'),

  logout: () => call<{ ok: true }>('/api/logout', { method: 'POST' }),

  palette: () => call<{ functions: unknown[] }>('/api/palette') as Promise<{ functions: import('./types').PaletteFunction[] }>,

  testset: () => call<{ set: import('./types').TestSet }>('/api/testset'),

  validate: (set: import('./types').TestSet) =>
    call<{ problems: import('./types').Problem[]; valid: boolean }>('/api/testset/validate', {
      method: 'POST',
      body: JSON.stringify({ set }),
    }),

  /** Throws an ApiError carrying `problems` when the server refuses the save. */
  saveTestset: async (set: import('./types').TestSet) => {
    const response = await fetch('/api/testset', {
      method: 'PUT',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ set }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new ApiError(body.error ?? 'Save refused', response.status) as ApiError & { problems?: unknown };
      err.problems = body.problems ?? [];
      throw err;
    }
    return body as { applied: boolean; version: string | null; problems: import('./types').Problem[]; warning?: string };
  },

  matrix: () => call<{ matrix: MatrixRow[]; applications: AppDecl[] }>('/api/matrix'),

  runs: (environment?: EnvironmentName) =>
    call<{ runs: RunSummary[] }>(`/api/runs${environment ? `?environment=${environment}` : ''}`),

  run: (id: string) => call<{ run: RunSummary & { outcomes: unknown[] } }>(`/api/runs/${id}`),

  start: (body: {
    environment: EnvironmentName;
    preset: PresetName;
    scope?: string;
    watch?: boolean;
    /** Capture a screenshot of every test, not only failures. */
    screenshots?: boolean;
    /**
     * Unit-economics inputs for a Performance run. Omitted unless supplied — the
     * report prefers an empty section to a cost derived from a guessed spend.
     */
    cost?: { monthlyUsd: string; requestsPerUser?: string; utilisation?: string };
  }) =>
    call<{ run: RunSummary; queued?: boolean; reason?: string; blockedBy?: { triggeredBy: string; label: string } }>(
      '/api/runs',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** The filmstrip for a finished run. Empty until it has one. */
  screenshots: (id: string) =>
    call<{ screenshots: Array<{ index: number; label: string }> }>(`/api/runs/${id}/screenshots`),

  /** The URL of one image — served by the console, so it stays behind auth. */
  screenshotUrl: (id: string, index: number) => `/api/runs/${id}/screenshots?index=${index}`,

  /**
   * Exportable reports for a Performance or Security run — the capacity/findings
   * report plus the raw k6/ZAP summary. Kept apart from `screenshots`: these are
   * documents, not images, and rendering one as a thumbnail is the bug this fixes.
   */
  reports: (id: string) =>
    call<{ reports: Array<{ index: number; label: string; contentType: string }> }>(
      `/api/runs/${id}/reports`,
    ),

  /** The URL of one report — served by the console, opens or downloads directly. */
  reportUrl: (id: string, index: number) => `/api/runs/${id}/reports?index=${index}`,

  /**
   * Builds the export URL, filtered — not a `call()`, since the response is a CSV
   * file with a `content-disposition: attachment`, not JSON. Used as an `<a href>`
   * so the browser downloads it directly, the same pattern `reportUrl`/`screenshotUrl`
   * already use for anything the server streams rather than returns as JSON.
   */
  exportUrl: (filters: { environment?: string; scope?: string; status?: 'all' | 'failed' }) => {
    const params = new URLSearchParams();
    if (filters.environment && filters.environment !== 'all') params.set('environment', filters.environment);
    if (filters.scope && filters.scope !== 'all') params.set('scope', filters.scope);
    params.set('status', filters.status ?? 'failed');
    return `/api/reports/export?${params.toString()}`;
  },

  stop: (id: string) => call<{ run: RunSummary }>(`/api/runs/${id}/stop`, { method: 'POST' }),

  /** Removes a finished run from history — refused (409) while it is still live. */
  deleteRun: (id: string) => call<{ ok: true }>(`/api/runs/${id}`, { method: 'DELETE' }),

  /** What the nightly is, and whether it can be started early. */
  nightly: () => call<NightlyState>('/api/nightly'),

  /**
   * Starts the real nightly workflow now.
   *
   * Throws an ApiError whose message is the refusal — production policy, a mutating
   * run already holding QA, one already in flight, or no token configured. The UI
   * shows that text rather than inventing its own, for the same reason the run
   * matrix renders the server's decision: two copies of a rule drift.
   */
  triggerNightly: () =>
    call<{ triggered: true; trigger: { at: string; by: string; workflowUrl: string } }>(
      '/api/nightly/trigger',
      { method: 'POST' },
    ),

  // ── recording a flow ─────────────────────────────────────────────────────
  //
  // No copy of the production rule lives here either, for the same reason `start()`
  // above has none: the server decides, and a refusal is rendered as the reason it
  // gave rather than a client-side guess about why.

  startRecording: (body: { app: string; environment: EnvironmentName; role: string }) =>
    call<{ recording: RecordingSummary }>('/api/recordings', { method: 'POST', body: JSON.stringify(body) }),

  recording: (id: string) => call<{ recording: RecordingSummary }>(`/api/recordings/${id}`),

  recordingLog: (id: string) => call<{ lines: LogLine[] }>(`/api/recordings/${id}/log`),

  stopRecording: (id: string) =>
    call<{ recording: RecordingSummary; draft?: RecordingDraft }>(`/api/recordings/${id}/stop`, { method: 'POST' }),

  recordingDraft: (id: string) => call<{ draft: RecordingDraft }>(`/api/recordings/${id}/draft`),

  /** Throws an ApiError with `.reason` when the draft has no assertion. */
  saveRecording: (
    id: string,
    body: { name: string; acceptedProposalIds?: string[]; tags?: string[]; mutating?: boolean },
  ) => call<{ applied: true; test: import('./types').TestCase }>(`/api/recordings/${id}/save`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  // ── running an authored test from the console ───────────────────────────

  tests: () => call<UnifiedInventory>('/api/tests'),

  runTest: (testId: string, environment: EnvironmentName, watch = false) =>
    call<{ run: RunSummary; queued?: boolean; reason?: string }>('/api/tests/run', {
      method: 'POST',
      body: JSON.stringify({ testId, environment, watch }),
    }),

  // ── QA tracker: bugs and enhancements ─────────────────────────────────────

  qaOptions: () => call<QaOptions>('/api/qa-tracker/options'),

  qaItems: (status: QaStatus, filters: QaItemFilters = {}) => {
    const params = new URLSearchParams({ status });
    if (filters.assignee) params.set('assignee', filters.assignee);
    if (filters.priority) params.set('priority', filters.priority);
    if (filters.itemType) params.set('itemType', filters.itemType);
    if (filters.module) params.set('module', filters.module);
    if (filters.reporter) params.set('reporter', filters.reporter);
    if (filters.search) params.set('search', filters.search);
    return call<{ items: QaItem[] }>(`/api/qa-tracker/items?${params.toString()}`);
  },

  qaItem: (id: number) => call<{ item: QaItem; activity: QaActivityEntry[] }>(`/api/qa-tracker/items/${id}`),

  /** Throws an ApiError with the server's validation message on refusal. */
  createQaItem: (input: QaItemDraft) =>
    call<{ item: QaItem }>('/api/qa-tracker/items', { method: 'POST', body: JSON.stringify(input) }),

  updateQaItem: (id: number, input: Partial<QaItemDraft>) =>
    call<{ item: QaItem }>(`/api/qa-tracker/items/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),

  reassignQaItem: (id: number, assignee: string) =>
    call<{ item: QaItem }>(`/api/qa-tracker/items/${id}/assignee`, {
      method: 'POST',
      body: JSON.stringify({ assignee }),
    }),

  /** Throws an ApiError (409) naming the allowed next step(s) when the move isn't valid from here. */
  transitionQaItem: (id: number, target: 'in_progress' | 'testable' | 'tbd' | 'done' | 'deferred' | 'qa_found_fixed' | 'backlog') =>
    call<{ item: QaItem }>(`/api/qa-tracker/items/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ target }),
    }),

  /** Permanent — a duplicate or mis-filed report. No confirmation server-side; the UI asks first. */
  deleteQaItem: (id: number) => call<{ ok: true }>(`/api/qa-tracker/items/${id}`, { method: 'DELETE' }),

  uploadQaAttachment: async (id: number, file: File) => {
    const params = new URLSearchParams({ filename: file.name, contentType: file.type || 'application/octet-stream' });
    const response = await fetch(`/api/qa-tracker/items/${id}/attachments?${params.toString()}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': file.type || 'application/octet-stream' },
      body: file,
    });
    const responseBody = await response.json().catch(() => ({}));
    if (!response.ok) throw new ApiError(responseBody.error ?? 'Upload failed', response.status);
    return responseBody as { item: QaItem };
  },

  deleteQaAttachment: (id: number, attachmentKey: string) =>
    call<{ item: QaItem }>(`/api/qa-tracker/items/${id}/attachments/${encodeURIComponent(attachmentKey)}`, {
      method: 'DELETE',
    }),

  /** The last path segment of the stored key is enough to identify it under one item. */
  qaAttachmentUrl: (id: number, attachmentKey: string) =>
    `/api/qa-tracker/items/${id}/attachments/${encodeURIComponent(attachmentKey.split('/').pop() ?? attachmentKey)}`,

  /** A plain `<a href>` download, same pattern `reportUrl`/`exportUrl` use for
   *  anything the server streams as a file rather than JSON. */
  qaTemplateUrl: () => '/api/qa-tracker/template',

  /** Always ADDS to the existing tracker list. Never throws for row-level
   *  problems — those come back in `problems` so the caller can show "created
   *  28, fix these 2 and re-upload" rather than an all-or-nothing failure. */
  importQaWorkbook: async (file: File) => {
    const response = await fetch('/api/qa-tracker/import', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      body: file,
    });
    const responseBody = await response.json().catch(() => ({}));
    if (!response.ok) throw new ApiError(responseBody.error ?? 'Import failed', response.status);
    return responseBody as { created: number; problems: Array<{ row: number; message: string }> };
  },

  // ── recipes (tiered-test-authoring §1) ────────────────────────────────────

  recipes: () => call<{ recipes: RecipeSummary[]; errors: string[] }>('/api/recipes'),

  recipe: (id: string) => call<RecipeDetail>(`/api/recipes/${encodeURIComponent(id)}`),

  /**
   * Writes a new `recipes/<id>.yaml`, validated against the schema before
   * anything reaches disk. Bypasses `call()` (matching `saveTestset`'s own
   * established pattern above) because a 422 here carries a real per-field
   * `errors` array the generic helper's `ApiError` does not have a slot for.
   */
  /** The exact real file bytes — used by "start from an existing recipe" when the GUI builder cannot represent one of its steps. */
  recipeYaml: (id: string) => call<{ yaml: string }>(`/api/recipes/${encodeURIComponent(id)}/yaml`),

  /** Same schema check `createRecipe` runs, minus the write — used by the GUI step builder's "Validate" button. */
  validateRecipeYaml: (yaml: string) =>
    call<{ valid: boolean; errors: string[] }>('/api/recipes/validate', {
      method: 'POST',
      body: JSON.stringify({ yaml }),
    }),

  /** Tier 2 -> Tier 3: only a promoted recipe is compiled into the nightly (scripts/compileRecipes.ts). */
  promoteRecipe: (id: string) =>
    call<{ recipe: RecipeSummary }>(`/api/recipes/${encodeURIComponent(id)}/promote`, { method: 'POST' }),

  createRecipe: async (id: string, yaml: string) => {
    const response = await fetch('/api/recipes', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id, yaml }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new ApiError(body.error ?? 'The recipe was refused.', response.status) as ApiError & { errors?: string[] };
      err.errors = body.errors ?? [];
      throw err;
    }
    return body as { recipe: RecipeSummary };
  },

  /** Refused with 409 if another recipe composes this one — see recipeRoutes.ts. */
  deleteRecipe: (id: string) => call<{ ok: true }>(`/api/recipes/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  compileRecipe: (id: string, variant?: string) =>
    call<{ testIds: string[]; preview: string[] }>(`/api/recipes/${encodeURIComponent(id)}/compile`, {
      method: 'POST',
      body: JSON.stringify({ variant }),
    }),

  /**
   * Runs through the REAL pipeline (tiered-test-authoring task 1.10) — the same
   * admission/policy/queue/start `/api/tests/run` uses. One entry per registered
   * TestCase id: a plain run or one variant produces exactly one; "run all
   * variants" produces one per variant, each its own independently reported Run.
   */
  runRecipe: (id: string, variant?: string, environment: string = 'qa', watch: boolean = false) =>
    call<{ runs: RecipeRunResult[] }>(`/api/recipes/${encodeURIComponent(id)}/run`, {
      method: 'POST',
      body: JSON.stringify({ variant, environment, watch }),
    }),

  // ── Tier 1 — the client recording extension (tiered-test-authoring §3) ────
  // These read/write the SAME unauthenticated `/api/tier1/*` routes the
  // extension itself calls — reviewing them here needs an ADMIN session (this
  // page is behind the console's normal sign-in), but nothing about the
  // ROUTES themselves requires one (see `tier1Routes.ts`'s own header).

  tier1Recordings: () => call<{ recordings: Tier1RecordingSummary[] }>('/api/tier1/recordings'),

  tier1Recording: (id: string) => call<{ recording: Tier1RecordingDetail }>(`/api/tier1/recordings/${encodeURIComponent(id)}`),

  reviewTier1Recording: (id: string, save: boolean) =>
    call<{ recording: Tier1RecordingDetail }>(`/api/tier1/recordings/${encodeURIComponent(id)}/review`, {
      method: 'POST',
      body: JSON.stringify({ save }),
    }),

  /** ADMIN-gated, unlike the routes above — see tier1Routes.ts's DELETE handler. */
  deleteTier1Recording: (id: string) =>
    call<{ ok: true }>(`/api/tier1/recordings/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /** Category/priority/comments/precondition — reviewer edits after submission. */
  updateTier1Meta: (id: string, patch: Partial<Pick<Tier1RecordingDetail, 'category' | 'priority' | 'comments' | 'precondition'>>) =>
    call<{ recording: Tier1RecordingDetail }>(`/api/tier1/recordings/${encodeURIComponent(id)}/meta`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  tier1Categories: () => call<{ categories: Tier1Category[] }>('/api/tier1/categories'),

  createTier1Category: (name: string) =>
    call<{ category: Tier1Category }>('/api/tier1/categories', { method: 'POST', body: JSON.stringify({ name }) }),

  renameTier1Category: (id: string, name: string) =>
    call<{ category: Tier1Category; oldName: string }>(`/api/tier1/categories/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),

  deleteTier1Category: (id: string) =>
    call<{ ok: true }>(`/api/tier1/categories/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /** A plain `<a href>` download, same pattern as `exportUrl` above. ADMIN session required, same as everything else in this tab. */
  tier1ExportUrl: () => '/api/tier1/export.xlsx',

  tier1Findings: () => call<{ findings: Tier1Finding[] }>('/api/tier1/findings'),

  tier1Snapshots: () => call<{ snapshots: Tier1SnapshotSummary[] }>('/api/tier1/snapshots'),

  tier1SnapshotPairs: () => call<{ pairs: Tier1SnapshotPair[] }>('/api/tier1/snapshots/pairs'),

  /** Step 1 of promoting a Tier 1 recording to a Tier 2 recipe — suggests only, writes nothing. */
  tier1VariableSuggestions: (id: string) =>
    call<{ suggestions: Array<{ stepIndex: number; suggestedName: string; reason: string }> }>(
      `/api/tier1/recordings/${encodeURIComponent(id)}/variable-suggestions`,
    ),

  /** Step 2 — builds the recipe YAML (does not write a file; the caller still has to `createRecipe` it). */
  promoteTier1ToRecipe: (id: string, acceptedStepIndices: number[]) =>
    call<{ recipeYaml: string }>(`/api/tier1/recordings/${encodeURIComponent(id)}/promote-to-recipe`, {
      method: 'POST',
      body: JSON.stringify({ acceptedStepIndices }),
    }),

  // ── test health and triage (tiered-test-authoring §5) ─────────────────────

  health: () => call<HealthDashboard>('/api/health'),

  failureGroups: (runId: string) => call<FailureGroups>(`/api/runs/${encodeURIComponent(runId)}/failure-groups`),

  // ── Extras: non-automatable tests list ─────────────────────────────────

  nonAutomatableTests: () => call<{ tests: NonAutomatableTest[] }>('/api/extras/non-automatable'),

  createNonAutomatableTest: (input: { name: string; category?: string; notes?: string }) =>
    call<{ test: NonAutomatableTest }>('/api/extras/non-automatable', { method: 'POST', body: JSON.stringify(input) }),

  deleteNonAutomatableTest: (id: string) =>
    call<{ ok: true }>(`/api/extras/non-automatable/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

export interface NonAutomatableTest {
  id: string;
  name: string;
  category: string | null;
  notes: string | null;
  createdBy: string;
  createdAt: string;
}

export interface LastRun {
  runId?: string;
  status: 'passed' | 'failed';
  at: string;
  error?: string;
}

export interface TestHealthEntry {
  testId: string;
  name: string;
  tier: 1 | 2 | 3;
  /** Which of the three authoring paths actually produced this test — the
   *  thing to show a person, since "tier" is this project's own internal
   *  bookkeeping term, not something anyone using the console should need
   *  to know. */
  origin: 'console' | 'code' | 'tier1';
  application: string;
  file?: string;
  line?: number;
  health: {
    runCount: number;
    passRate30d: number | null;
    avgDurationMs: number | null;
    flakinessScore: number | null;
    lastRunAt: string | null;
    stale: boolean;
    likelyBroken: boolean;
  };
  ownership: { kind: string; who: string | null; detail?: string };
  scheduledCadence: 'nightly' | 'weekly' | 'never';
  valueTier: 'high' | 'low';
  lastRun: LastRun | null;
  lastFailure: LastRun | null;
}

export interface HealthDashboard {
  tests: TestHealthEntry[];
  flaky: TestHealthEntry[];
  staleOver30Days: TestHealthEntry[];
  alwaysPassing: TestHealthEntry[];
  highValueButFailing: TestHealthEntry[];
}

export interface FailureGroups {
  groups: Array<{ reason: string; urlPattern?: string; errorSignature?: string; failures: Array<{ testId: string; testName: string }> }>;
  ungrouped: Array<{ testId: string; testName: string }>;
}

export interface Tier1SnapshotFull {
  id: string;
  app: string;
  environment: 'dev' | 'qa';
  path: string;
  screenshot: string;
  capturedAt: string;
}

export interface Tier1SnapshotPair {
  app: string;
  path: string;
  before: Tier1SnapshotFull;
  after: Tier1SnapshotFull | null;
  count: number;
}

export interface Tier1Category {
  id: string;
  name: string;
}

export interface Tier1Precondition {
  requiresLogin: boolean;
  startPath: string;
  role?: string;
  note?: string;
}

export interface Tier1RecordingSummary {
  id: string;
  app: string;
  environment: 'dev' | 'qa';
  startedAt: string;
  finishedAt: string;
  stepCount: number;
  findingCount: number;
  reviewed: boolean;
  savedAsTestId: string | null;
  name: string | null;
  category: string | null;
  priority: 'low' | 'medium' | 'high' | 'critical' | null;
  comments: string | null;
  precondition: Tier1Precondition | null;
}

export interface Tier1Step {
  index: number;
  description: string;
  path: string;
  screenshot: string;
  brokenNote?: string;
}

export interface Tier1RecordingDetail extends Tier1RecordingSummary {
  steps: Tier1Step[];
  findings: Array<{ stepIndex: number; note: string }>;
}

export interface Tier1Finding {
  id: string;
  recordingId: string;
  stepIndex: number;
  note: string;
  filedAt: string;
}

export interface Tier1SnapshotSummary {
  id: string;
  app: string;
  environment: 'dev' | 'qa';
  path: string;
  capturedAt: string;
}

export interface RecipeRunResult {
  testId: string;
  run?: RunSummary;
  queued?: boolean;
  reason?: string;
  error?: string;
  refusedBy?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Recipes (tiered-test-authoring §1)
// ─────────────────────────────────────────────────────────────────────────────

export interface RecipeHealth {
  recipeId: string;
  runCount: number;
  passRate30d: number | null;
  avgDurationMs: number | null;
  flakinessScore: number | null;
  lastRunAt: string | null;
  stale: boolean;
  likelyBroken: boolean;
}

export interface RecipeSummary {
  id: string;
  name: string;
  description: string | null;
  application: string;
  tags: string[];
  mutating: boolean;
  composes: string[];
  /** True for a compose-only base (e.g. login-core) — cannot run standalone by design. */
  composeOnly: boolean;
  /** Non-null when compilation fails because a real credential env var is missing — distinct from composeOnly. */
  credentialError: string | null;
  hasDataTable: boolean;
  variants: string[];
  health: RecipeHealth;
  /** Tier 2 -> Tier 3 gate — only a promoted recipe is compiled into the nightly. */
  promoted: boolean;
}

/** Mirrors console/shared/recipes.ts's ParamValue — a client-side copy since
 *  the web bundle does not import server-only shared modules for this. */
export type RecipeParamValue =
  | { kind: 'literal'; value: string | number | boolean | null }
  | { kind: 'variable'; name: string }
  | { kind: 'expression'; source: string }
  | { kind: 'column'; column: string };

export interface RecipeStepDetail {
  id?: string;
  functionName: string;
  label?: string;
  params?: Record<string, RecipeParamValue>;
  screenshotAssertion?: { name: string; description?: string; target?: string };
  timing?: { retryCount?: number; retryIntervalMs?: number };
}

export interface RecipeDetail {
  recipe: {
    id: string;
    name: string;
    description?: string;
    application: string;
    tags?: string[];
    mutating?: boolean;
    composes?: string[];
    variables?: Array<{ name: string; type: string; required?: boolean; description?: string }>;
    steps?: RecipeStepDetail[];
    dataTable?: { columns: string[]; rows: Array<Record<string, string>> };
    variants?: Array<{ name: string; values: Record<string, string | number | boolean> }>;
    promoted?: boolean;
  };
  compositionOrder: string[];
  stepCount: number | null;
  compileError: string | null;
  health: RecipeHealth;
  schemaErrors: string[];
}

export type QaItemType = 'bug' | 'enhancement';
export type QaPriority = 'low' | 'medium' | 'high' | 'critical';
export type QaEnvironment = 'Dev' | 'QA' | 'Demo' | 'Production';
export type QaStatus = 'backlog' | 'in_progress' | 'testable' | 'tbd' | 'archived';

export interface QaOptions {
  assignees: string[];
  priorities: QaPriority[];
  itemTypes: QaItemType[];
  environments: QaEnvironment[];
  statuses: QaStatus[];
  modules: string[];
  reporters: string[];
}

export interface QaItemFilters {
  assignee?: string;
  priority?: string;
  itemType?: string;
  module?: string;
  reporter?: string;
  search?: string;
}

export interface QaAttachment {
  key: string;
  name: string;
  contentType: string;
  size: number;
  uploadedAt: string;
}

export interface QaItemDraft {
  title: string;
  itemType: QaItemType;
  description: string;
  stepsToReproduce?: string;
  expectedResult?: string;
  actualResult?: string;
  currentBehavior?: string;
  proposedImprovement?: string;
  benefit?: string;
  priority: QaPriority;
  assignee: string;
  reporter?: string;
  module: string;
  environment: QaEnvironment;
}

export interface QaItem extends QaItemDraft {
  id: number;
  /** The human-facing ticket number ("B-1", "E-1") — say this in a standup, not `id`. */
  displayId: string;
  reporter: string;
  attachments: QaAttachment[];
  status: QaStatus;
  resolution?: 'done' | 'deferred' | 'qa_found_fixed';
  createdAt: string;
  updatedAt: string;
  archivedAt?: string;
}

export interface QaActivityEntry {
  id: number;
  at: string;
  actor: string;
  action: 'created' | 'assigned' | 'status_changed';
  fromValue?: string;
  toValue?: string;
}

export interface RecordingSummary {
  id: string;
  status: 'starting' | 'recording' | 'stopping' | 'ready' | 'error' | 'expired';
  app: string;
  environment: EnvironmentName;
  role: string;
  triggeredBy: string;
  startedAt: string;
  finishedAt?: string;
  expiresAt: string;
  vncUrl?: string;
  stepCount: number;
  assertionCount: number;
  error?: string;
  hasDraft: boolean;
}

export interface RecordingDraftStep {
  functionName: string;
  params: Record<string, { kind: 'literal'; value: string | number | boolean }>;
  label?: string;
  selectorWarning?: string;
  nth?: number;
  assertion?: boolean;
  selectorStrategy?: 'role' | 'label' | 'testid' | 'placeholder' | 'text' | 'css';
  durable?: boolean;
}

export interface MatchProposalSummary {
  id: string;
  functionName: string;
  className: string;
  method: string;
  file: string;
  range: { start: number; end: number };
  replaces: RecordingDraftStep[];
  replacement: RecordingDraftStep;
  confidence: 'high' | 'medium' | 'low';
  reasons: string[];
  caveats: string[];
}

export interface RecordingDraft {
  steps: RecordingDraftStep[];
  proposals: MatchProposalSummary[];
  missing: Array<{ key: string; description: string; application: string }>;
  dropped: Array<{ description: string; reason: string }>;
  application: string;
  assertionCount: number;
  capturedCount: number;
}

export interface UnifiedInventory {
  tests: Array<{
    id: string; name: string; origin: 'console' | 'code'; application: string;
    tags: string[]; enabled: boolean; mutating: boolean; steps?: number;
    file?: string; line?: number; promotedTo?: string; runnable: boolean;
  }>;
  counts: { total: number; console: number; code: number; disabled: number };
  duplicates: Array<{ name: string; origins: Array<'console' | 'code'> }>;
}

export interface NightlyState {
  selection: { environment: string; preset: string; scope: string };
  workflow: { repo: string; file: string; ref: string; url: string };
  available: boolean;
  unavailableReason: string | null;
  inFlight: boolean;
  lastTrigger: { at: string; by: string; workflowUrl: string } | null;
}

/**
 * Subscribes to a run's log stream.
 *
 * EventSource reconnects on its own and replays `Last-Event-ID`, so output produced
 * while the tab was asleep or the network dropped arrives on reconnect rather than
 * being lost (task 13.12). That is the whole reason this is SSE and not a socket.
 */
export function streamLogs(runId: string, onLine: (line: LogLine) => void): () => void {
  const source = new EventSource(`/api/runs/${runId}/stream`, { withCredentials: true });

  source.addEventListener('log', (event) => {
    try {
      onLine(JSON.parse((event as MessageEvent).data) as LogLine);
    } catch {
      /* a malformed frame must not kill the stream */
    }
  });

  return () => source.close();
}
